import { SystemEntity } from '@erp/shared-types';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { FLOW_IN_SOCKET_ID, FLOW_OUT_SOCKET_ID } from '@erp/workflows';
import { cn } from '@/lib/utils';

interface StepArtifactsDisplayProps {
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  className?: string;
}

function isEntityReference(
  value: unknown,
): { entityType: SystemEntity; entityId: string } | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (
      typeof obj.entityType === 'string' &&
      typeof obj.entityId === 'string' &&
      Object.values(SystemEntity).includes(obj.entityType as SystemEntity)
    ) {
      return {
        entityType: obj.entityType as SystemEntity,
        entityId: obj.entityId as string,
      };
    }
  }
  return null;
}

function isSimpleType(value: unknown): boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  );
}

function formatSimpleValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') {
    return value.length > 100 ? value.slice(0, 100) + '...' : value;
  }
  return String(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasNestedEntityRefs(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some(
    (v) =>
      isEntityReference(v) !== null ||
      (isPlainObject(v) && hasNestedEntityRefs(v)),
  );
}

function ArtifactValue({ value }: { value: unknown }) {
  const entityRef = isEntityReference(value);
  if (entityRef) {
    return (
      <SystemEntityInline
        entityType={entityRef.entityType}
        entityId={entityRef.entityId}
      />
    );
  }

  if (Array.isArray(value)) {
    const entityRefs = value
      .map((item) => isEntityReference(item))
      .filter((ref): ref is NonNullable<typeof ref> => ref !== null);

    if (entityRefs.length > 0 && entityRefs.length === value.length) {
      return (
        <div className="flex flex-wrap gap-1">
          {entityRefs.map((ref, i) => (
            <SystemEntityInline
              key={i}
              entityType={ref.entityType}
              entityId={ref.entityId}
            />
          ))}
        </div>
      );
    }
  }

  if (isSimpleType(value)) {
    return (
      <span className="text-sm text-foreground">
        {formatSimpleValue(value)}
      </span>
    );
  }

  if (isPlainObject(value) && hasNestedEntityRefs(value)) {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-muted">
        {Object.entries(value).map(([key, nestedValue]) => (
          <div key={key} className="flex items-start gap-2">
            <span className="text-xs font-mono text-muted-foreground min-w-[60px] shrink-0">
              {key}:
            </span>
            <div className="flex-1 min-w-0">
              <ArtifactValue value={nestedValue} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-w-md">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function ArtifactSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown>;
}) {
  const filteredEntries = Object.entries(data).filter(
    ([key]) => key !== FLOW_IN_SOCKET_ID && key !== FLOW_OUT_SOCKET_ID,
  );

  if (filteredEntries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h6 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </h6>
      <div className="space-y-1.5">
        {filteredEntries.map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-sm font-mono text-muted-foreground shrink-0">
              {key}:
            </span>
            <ArtifactValue value={value} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function StepArtifactsDisplay({
  inputData,
  outputData,
  className,
}: StepArtifactsDisplayProps) {
  const hasInputs =
    inputData &&
    Object.keys(inputData).filter(
      (k) => k !== FLOW_IN_SOCKET_ID && k !== FLOW_OUT_SOCKET_ID,
    ).length > 0;

  const hasOutputs =
    outputData &&
    Object.keys(outputData).filter(
      (k) => k !== FLOW_IN_SOCKET_ID && k !== FLOW_OUT_SOCKET_ID,
    ).length > 0;

  if (!hasInputs && !hasOutputs) {
    return <p className="text-xs text-muted-foreground italic">No artifacts</p>;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {hasInputs && inputData && (
        <ArtifactSection title="Inputs" data={inputData} />
      )}
      {hasOutputs && outputData && (
        <ArtifactSection title="Outputs" data={outputData} />
      )}
    </div>
  );
}
